import type { GuestMode } from './guestMode'
import { tenantApiBaseUrl } from './apiBaseUrl'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
/** Where each flow keeps the guest's visitor code (each flow's own key, unchanged). */
const VISITOR_KEY: Record<GuestMode, string> = { temporary: 'memo_visitor_code', permanent: 'memo.visitorCode' }

function storedVisitorCode(): string | null {
  try {
    for (const key of [VISITOR_KEY.permanent, VISITOR_KEY.temporary]) {
      const code = localStorage.getItem(key)
      if (code && UUID.test(code)) return code
    }
  } catch {
    // storage blocked: the API's cookie still identifies the guest
  }
  return null
}

/**
 * Settle an old bare link (`/<code>`): ask the API which kind of event the code belongs to.
 * Returns undefined when it can't be settled (no code, unknown code, a failed call, or a backend
 * that doesn't send `type` yet); the caller then does exactly what this host did before.
 * A visitor code the API issues on the way is kept under the flow's own key, so the flow's first
 * call carries the same guest instead of being issued a second code. On a tenant host it asks that
 * tenant's API, as both flows do.
 */
export async function resolveOldLink(
  code: string,
  apiBase: string = tenantApiBaseUrl(window.location.hostname, import.meta.env.VITE_API_BASE_URL),
): Promise<GuestMode | undefined> {
  if (!code) return undefined
  const visitor = storedVisitorCode()
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), 10_000)
  try {
    const res = await fetch(`${apiBase}/public/experiences/${encodeURIComponent(code)}`, {
      headers: visitor ? { 'X-Visitor-Code': visitor } : {},
      credentials: 'include',
      signal: ctrl.signal,
    })
    if (!res.ok) return undefined
    const body = (await res.json()) as { data?: { type?: unknown } }
    const type = body?.data?.type
    if (type !== 'temporary' && type !== 'permanent') return undefined
    const issued = res.headers.get('X-Visitor-Code')
    if (!visitor && issued && UUID.test(issued)) {
      try {
        localStorage.setItem(VISITOR_KEY[type], issued)
      } catch {
        // storage blocked: nothing to keep
      }
    }
    return type
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}
